<template>
  <v-card-text>
    <!-- <div class="mb-4 pa-4">
      <h1
        :class="xs ? 'text-h5' : 'text-h4'"
        class="font-weight-bold text-2c3e50 mb-1"
      >
        Demographics Overview
      </h1>
      <p class="text-muted" :class="xs ? 'text-xs' : 'text-sm'">
        System level:
        <v-chip
          v-if="dashboardData.metrics?.level"
          size="small"
          color="primary"
          variant="flat"
          class="ml-1"
        >
          {{ dashboardData.metrics.level }}
        </v-chip>
      </p> 
    </div> -->

    <v-row>
      <v-col cols="12" sm="6" md="3">
        <stat-card
          label="Total Polls"
          :value="dashboardData.counts?.total_polls || 0"
          icon="mdi-poll"
          gradient="primary"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <stat-card
          label="Live"
          :value="dashboardData.counts?.total_live || 0"
          icon="mdi-access-point"
          gradient="success"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <stat-card
          label="Completed"
          :value="dashboardData.counts?.total_completed || 0"
          icon="mdi-check-circle"
          gradient="info"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <stat-card
          label="Total Votes"
          :value="dashboardData.counts?.total_votes || 0"
          icon="mdi-vote"
          gradient="warning"
        />
      </v-col>
    </v-row>

    <v-row class="pb-4">
      <v-col cols="12" md="4">
        <v-card class="custom-card border-card" elevation="0" height="340">
          <v-card-text class="pa-4">
            <h3 class="custom-card-title mb-1 text-subtitle-1 font-weight-bold">
              Age Distribution
            </h3>
            <p class="text-muted text-caption mb-4">Users segmented by age</p>
            <apexchart
              type="bar"
              height="220"
              :options="ageChartOptions"
              :series="ageChartSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="custom-card border-card" elevation="0" height="340">
          <v-card-text class="pa-4">
            <h3 class="custom-card-title mb-1 text-subtitle-1 font-weight-bold">
              Platform Usage
            </h3>
            <p class="text-muted text-caption mb-4">Android vs iOS</p>
            <apexchart
              type="donut"
              height="240"
              :options="deviceChartOptions"
              :series="deviceChartSeries"
            ></apexchart>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card class="custom-card border-card" elevation="0" height="340">
          <v-card-text class="pa-4">
            <h3 class="custom-card-title mb-1 text-subtitle-1 font-weight-bold">
              Location Breakdown
            </h3>
            <p class="text-muted text-caption mb-4">Geographic distribution</p>

            <div class="top-polls-list pr-2">
              <div
                v-for="(loc, idx) in locationList"
                :key="idx"
                class="poll-item mb-4 pb-3"
                :style="{
                  borderBottom:
                    idx !== locationList.length - 1
                      ? '1px solid #ecf0f1'
                      : 'none',
                }"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <span class="text-semibold text-sm">{{ loc.label }}</span>
                  <v-chip
                    size="x-small"
                    variant="tonal"
                    :color="loc.color"
                    class="font-weight-bold"
                  >
                    {{ loc.count }} ({{ loc.percentage }}%)
                  </v-chip>
                </div>
                <v-progress-linear
                  :model-value="loc.percentage"
                  :color="loc.color"
                  height="6"
                  rounded
                ></v-progress-linear>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card-text>
</template>

<script>
import { useDisplay } from "vuetify";
import VueApexCharts from "vue3-apexcharts";
import StatCard from "@/components/Dashboards/Cards/StatCard.vue";
import { GetDashboardMetrics } from "@/mixins/Dashboards/GetDashboardMetrics";

export default {
  name: "DashboardPage",
  components: {
    apexchart: VueApexCharts,
    StatCard,
  },
  mixins: [GetDashboardMetrics],
  setup() {
    const { xs } = useDisplay();
    return { xs };
  },
  computed: {
    // Bar Chart: Age
    ageChartSeries() {
      const items = this.dashboardData.metrics?.age_distribution?.items || [];
      return [{ name: "Users", data: items.map((i) => i.count) }];
    },
    ageChartOptions() {
      const items = this.dashboardData.metrics?.age_distribution?.items || [];
      return {
        chart: {
          type: "bar",
          toolbar: { show: false },
          fontFamily: "Roboto, sans-serif",
        },
        colors: ["#667eea"],
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "45%",
            dataLabels: { position: "top" },
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: { fontSize: "12px", colors: ["#304758"] },
        },
        xaxis: {
          categories: items.map((i) => i.label),
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { show: false },
        grid: { show: false },
        tooltip: { theme: "light" },
      };
    },

    // Donut Chart: Device
    deviceChartSeries() {
      const items =
        this.dashboardData.metrics?.device_distribution?.items || [];
      return items.map((i) => i.count);
    },
    deviceChartOptions() {
      const items =
        this.dashboardData.metrics?.device_distribution?.items || [];
      return {
        chart: { type: "donut", fontFamily: "Roboto, sans-serif" },
        colors: ["#27ae60", "#2980b9", "#f39c12"],
        labels: items.map((i) => i.label),
        legend: { position: "bottom", fontSize: "13px" },
        stroke: { width: 0 },
        dataLabels: {
          enabled: true,
          formatter: (val) => val.toFixed(0) + "%",
          dropShadow: { enabled: false },
        },
        plotOptions: { pie: { donut: { size: "70%" } } },
      };
    },

    // List: Location
    locationList() {
      const overall =
        this.dashboardData.metrics?.location_distribution?.overall || 1;
      const items =
        this.dashboardData.metrics?.location_distribution?.items || [];
      const colors = ["primary", "warning", "success", "info"];

      return [...items]
        .sort((a, b) => b.count - a.count)
        .map((item, idx) => ({
          label: item.label,
          count: item.count,
          percentage: Math.round((item.count / overall) * 100),
          color: colors[idx % colors.length],
        }));
    },
  },
  async mounted() {
    // Note: Assuming your mixin methods return the respective objects
    this.dashboardData.metrics = await this.GetMetricsDataMethod();
    this.dashboardData.counts = await this.GetCountsDataMethod();
    // console.log("DATA : ", this.dashboardData);
  },
  data() {
    return {
      // Mock data structured to match the expected API response
      dashboardData: {},
    };
  },
};
</script>

<style scoped>
.text-2c3e50 {
  color: #2c3e50;
}
.text-muted {
  color: #888888;
}
.border-card {
  border: 1px solid #edf2f9;
  border-radius: 12px;
  background-color: #ffffff;
}
.top-polls-list {
  max-height: 230px;
  overflow-y: auto;
}
/* Custom Scrollbar for the location list */
.top-polls-list::-webkit-scrollbar {
  width: 4px;
}
.top-polls-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.top-polls-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.poll-item {
  transition: all 0.2s ease;
}
.poll-item:hover {
  background-color: #f8fafc;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 6px;
}
.text-semibold {
  font-weight: 600;
}
</style>

<!-- <template>
  <v-container
    class="fill-height d-flex justify-center align-center"
    style="min-height: 60vh"
  >
    <v-card
      class="rounded-xl border pa-8 text-center bg-white"
      max-width="450"
      elevation="0"
    >
      <v-avatar color="amber-lighten-4" size="80" class="mb-5">
        <v-icon size="40" color="amber-darken-3">mdi-hammer-wrench</v-icon>
      </v-avatar>

      <h2 class="text-h5 font-weight-bold text-grey-darken-3 mb-2">
        Work in Progress
      </h2>

      <p class="text-body-1 text-grey-darken-1 mb-6">
        We are currently brewing up something awesome here. This feature will be
        available very soon!
      </p>

      <v-btn
        color="primary"
        variant="tonal"
        prepend-icon="mdi-arrow-left"
        class="text-none font-weight-bold px-6 rounded-lg"
        size="large"
        @click="$router.back()"
      >
        Go Back
      </v-btn>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "WorkInProgressCard",
};
</script>

<style scoped>
/* Optional: Adds a subtle pulse animation to the icon to make it feel "active" */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.v-avatar {
  animation: pulse 2.5s infinite ease-in-out;
}
</style> -->
